/**
 * VersionHistoryDrawer
 * ─────────────────────────────────────────────────────────────────────────────
 * Reusable drawer that renders the audit history for ONE entity (e.g., a
 * product) so vendors can inspect every change without leaving the edit page.
 *
 * Usage:
 *   <VersionHistoryDrawer
 *     show={show}
 *     onHide={() => setShow(false)}
 *     entityType="RestaurantProduct"
 *     entityId={product._id}
 *     entityLabel={product.name}
 *   />
 *
 * Philosophy:
 *   - Read-only. Never writes. Backend is source of truth.
 *   - Feature-flag parent: REACT_APP_FEATURE_AUDIT_TRAIL (caller gates mount).
 *   - Row detail expansion reuses /api/audit/vendor/activity/:id so list
 *     payloads stay trim.
 *   - Diff rendered as {field, before, after} table — easy for a non-technical
 *     vendor to read at a glance.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Button,
  Spinner,
  Badge,
  Alert,
  Collapse,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { apiGetVendorActivity, apiGetActivityRow } from "../api/index";

// Keep labels aligned with the feed view (single source of truth for display
// strings; an eventual refactor should hoist these to a shared module).
const ACTION_LABELS = {
  PRODUCT_CREATE: "Product created",
  PRODUCT_ARCHIVE: "Product archived",
  PRODUCT_RENAME: "Product renamed",
  PRODUCT_DESCRIPTION_EDIT: "Description edited",
  PRODUCT_IMAGE_ADD: "Image updated",
  PRODUCT_IMAGE_REMOVE: "Image removed",
  PRODUCT_CATEGORY_CHANGE: "Category changed",
  PRODUCT_ACTIVATE: "Product activated",
  PRODUCT_DEACTIVATE: "Product deactivated",
  PRODUCT_STOCK_THRESHOLD_CHANGE: "Stock threshold changed",
  PRICE_CHANGE: "Price changed",
  COMMISSION_RATE_CHANGE: "Commission rate changed",
  HOURS_CHANGE: "Hours changed",
  OUTLET_OPEN: "Outlet opened",
  OUTLET_CLOSE: "Outlet closed",
};

const PAGE_SIZE = 20;

function formatValue(v) {
  if (v === null || v === undefined) return <em style={{ color: "#999" }}>empty</em>;
  if (typeof v === "boolean") return v ? "✓ Yes" : "✗ No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "object") {
    try {
      return <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>;
    } catch {
      return String(v);
    }
  }
  const s = String(v);
  return s.length > 120 ? s.slice(0, 117) + "…" : s;
}

function DiffTable({ diff }) {
  if (!diff || typeof diff !== "object") return null;
  const keys = Object.keys(diff);
  if (keys.length === 0) {
    return <div className="text-muted small">No field-level diff recorded.</div>;
  }
  return (
    <Table size="sm" bordered hover className="mb-0" style={{ fontSize: 13 }}>
      <thead style={{ background: "#f7f7f9" }}>
        <tr>
          <th style={{ width: "25%" }}>Field</th>
          <th style={{ width: "37.5%" }}>Before</th>
          <th style={{ width: "37.5%" }}>After</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const d = diff[k] || {};
          return (
            <tr key={k}>
              <td><strong>{k}</strong></td>
              <td style={{ background: "#fff5f5" }}>{formatValue(d.from)}</td>
              <td style={{ background: "#f0fff4" }}>{formatValue(d.to)}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function RowItem({ row }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailErr, setDetailErr] = useState(null);

  const needsDetail = !row.diff || (!row.before && !row.after);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && needsDetail && !detail && !loadingDetail) {
      setLoadingDetail(true);
      setDetailErr(null);
      try {
        const res = await apiGetActivityRow(row._id);
        setDetail(res?.data?.data || res?.data || null);
      } catch (e) {
        setDetailErr(e?.response?.data?.message || e.message || "Failed to load details");
      } finally {
        setLoadingDetail(false);
      }
    }
  }, [open, needsDetail, detail, loadingDetail, row._id]);

  const data = detail || row;
  const actionLabel = ACTION_LABELS[row.action] || row.action;
  const actorName = row.actorName || row.actor?.name || "Unknown";

  return (
    <div
      style={{
        borderLeft: "3px solid #2d7a5f",
        paddingLeft: 12,
        marginBottom: 14,
        background: "#fafbfc",
        borderRadius: 4,
        padding: "10px 12px",
      }}
    >
      <div
        style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        onClick={toggle}
      >
        <div>
          <strong>{actionLabel}</strong>
          {row.reason && (
            <span style={{ marginLeft: 8, color: "#8a6d3b", fontSize: 12 }}>
              — {row.reason.length > 60 ? row.reason.slice(0, 57) + "…" : row.reason}
            </span>
          )}
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
            by <strong>{actorName}</strong>
            {row.actorType && <Badge bg="light" text="dark" className="ms-1" style={{ fontSize: 10 }}>{row.actorType}</Badge>}
            {" · "}
            {moment(row.createdAt).format("MMM D, YYYY h:mm A")}
            {" · "}
            <span style={{ color: "#999" }}>{moment(row.createdAt).fromNow()}</span>
          </div>
        </div>
        <i className={`fa fa-chevron-${open ? "up" : "down"}`} style={{ color: "#999" }} />
      </div>

      <Collapse in={open}>
        <div style={{ marginTop: 10 }}>
          {loadingDetail && <Spinner size="sm" animation="border" />}
          {detailErr && <Alert variant="warning" className="py-1 px-2 small mb-2">{detailErr}</Alert>}
          {!loadingDetail && !detailErr && <DiffTable diff={data?.diff} />}
          {data?.request?.ip && (
            <div className="text-muted mt-2" style={{ fontSize: 11 }}>
              IP: <code>{data.request.ip}</code>
              {data.request.userAgent && <> · <span title={data.request.userAgent}>{String(data.request.userAgent).slice(0, 40)}…</span></>}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export default function VersionHistoryDrawer({
  show,
  onHide,
  entityType,
  entityId,
  entityLabel,
}) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(
    async (p) => {
      if (!entityType || !entityId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiGetVendorActivity({
          entityType,
          entityId,
          page: p,
          limit: PAGE_SIZE,
        });
        const payload = res?.data || {};
        const nextRows = payload.data || [];
        setRows((prev) => (p === 1 ? nextRows : [...prev, ...nextRows]));
        setHasMore(!!payload.hasMore);
        setPage(p);
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Failed to load history");
      } finally {
        setLoading(false);
      }
    },
    [entityType, entityId]
  );

  useEffect(() => {
    if (show) {
      setRows([]);
      setPage(1);
      setHasMore(false);
      fetchPage(1);
    }
  }, [show, fetchPage]);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-history mr-2" /> Version History
          {entityLabel && (
            <span style={{ fontWeight: 400, fontSize: 14, color: "#666", marginLeft: 10 }}>
              — {entityLabel}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3" style={{ fontSize: 13 }}>
          Every change made to this item is recorded here — who changed what, when,
          and why. This log is <strong>immutable</strong> and exportable for your records.
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {!error && rows.length === 0 && !loading && (
          <Alert variant="light" className="text-center">
            <div style={{ fontSize: 32 }}>🗂️</div>
            <strong>No changes recorded yet</strong>
            <div className="small text-muted mt-1">
              As soon as anyone edits this item, the change will appear here.
            </div>
          </Alert>
        )}

        {rows.map((row) => (
          <RowItem key={row._id} row={row} />
        ))}

        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        )}

        {!loading && hasMore && (
          <div className="text-center mt-3">
            <Button variant="outline-secondary" size="sm" onClick={() => fetchPage(page + 1)}>
              Load more
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
